import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table } from 'antd';
import { formatDate, getFullName } from 'utils';
import { DATE_FORMAT, EMDASH } from 'constants';
import { parseFamilyData } from 'utils';
import { FAMILY_IMPORT_STATUS_MAP } from 'containers/Family/AddApplication2/familyApplicationConstants';

export default function FamilyDashboardTable({ state, loading, className, sorterInfo, ...rest }) {
  const data = useMemo(() => {
    return (state.data || []).map(parseFamilyData);
  }, [state.data]);

  const columns = useMemo(() => getFamilyDashboardColumns(sorterInfo), [sorterInfo]);

  return (
    <Table
      {...rest}
      columns={columns}
      rowKey="id"
      dataSource={data}
      loading={state.loading || loading}
      pagination={state.pagination}
      onChange={state.onChange}
      expandable={{
        rowExpandable: () => false,
        showExpandColumn: false,
      }}
      showColSeparator={false}
      scroll={{ x: 'max-content' }}
      locale={{
        filterReset: 'Clear',
        filterConfirm: 'Apply filters',
      }}
    />
  );
}

function getFamilyDashboardColumns(sorterInfo) {
  return [
    {
      title: 'App ID',
      headerText: 'App ID',
      dataIndex: 'id',
      key: 'caseId',
      sorter: true,
      width: 110,
      render: (id, record) => (
        <Link
          onClick={(e) => e.stopPropagation()}
          className="underline px-3 py-1"
          to={`/families/${record.householdId}/applications/${id}`}
        >
          {id}
        </Link>
      ),
      sortOrder: sorterInfo?.columnKey === 'caseId' && sorterInfo?.order,
    },
    {
      title: 'Family ID',
      headerText: 'Family ID',
      dataIndex: ['householdId'],
      key: 'familyId',
      sorter: true,
      width: 120,
      render: (householdId) => (
        <Link
          onClick={(e) => e.stopPropagation()}
          className="underline px-3 py-1 text-blue-600 hover:text-blue-800"
          to={`/families/household/${householdId}/applications`}
        >
          {householdId}
        </Link>
      ),
      sortOrder: sorterInfo?.columnKey === 'familyId' && sorterInfo?.order,
    },
    {
      title: 'Branch',
      headerText: 'Branch',
      key: 'branch',
      width: 140,
      render: (_, record) => {
        const branchName = record?.programSponsor?.title;
        return branchName || EMDASH;
      },
    },
    {
      title: 'Sponsor',
      headerText: 'Sponsor',
      dataIndex: 'applicant.firstName',
      key: 'sponsor',
      sorter: true,
      className: 'truncate max-w-xs',
      render: (firstName, { applicant }) => {
        return (
          <span className="truncate max-w-xs" title={applicant ? getFullName(applicant) : undefined}>
            {getFullName(applicant)}
          </span>
        );
      },
      width: 170,
      sortOrder: sorterInfo?.columnKey === 'sponsor' && sorterInfo?.order,
    },
    {
      title: 'Status',
      headerText: 'Status',
      dataIndex: 'status.status.description',
      key: 'status',
      width: 160,
      sorter: true,
      className: 'truncate max-w-xs',
      render: (_, record) => {
        const status = record?.status?.status?.description;
        return status ? <span title={status}>{status}</span> : 'N/A';
      },
      sortOrder: sorterInfo?.columnKey === 'status' && sorterInfo?.order,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      sorter: true,
      width: 150,
      render: (date, record) => {
        const displayDate = date || record?.startDate;
        return <span>{formatDate(displayDate, DATE_FORMAT[1], EMDASH)}</span>;
      },
      sortOrder: sorterInfo?.columnKey === 'createdDate' && sorterInfo?.order,
    },
    {
      title: 'Submitted Date',
      dataIndex: 'submittedDate',
      key: 'submittedDate',
      sorter: true,
      width: 180,
      render: (date) => <span>{formatDate(date, DATE_FORMAT[1], EMDASH)}</span>,
      sortOrder: sorterInfo?.columnKey === 'submittedDate' && sorterInfo?.order,
    },
    {
      title: 'Import Status',
      key: 'importStatus',
      width: 170,
      render: (_, record) => {
        const importStatus = record?.importStatus;
        return importStatus ? FAMILY_IMPORT_STATUS_MAP[importStatus]?.label || importStatus : EMDASH;
      },
    },
  ];
}
